import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Info, MapPin, Plane, RotateCcw, Ship, Truck } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils/cn';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { MarcoDeComercia } from '../components/marco-de-comercia';
import { EncabezadoDeComercia } from '../components/encabezado-de-comercia';
import { PasosDeLogistica } from '../components/pasos-de-logistica';
import { MapaDeRuta } from '../components/logistica/mapa-de-ruta';
import { comerciaService } from '../services/comercia-service';
import { useImportacion } from '../store/importacion-store';
import type { LugarDeComercia, LugaresDeComercia, ViasDeComercia } from '../types/comercia.types';
import {
  ciudadDe,
  modoDelPunto,
  nombreDePais,
  nombreDelPunto,
  rotuloDelPunto,
} from '../utils/lugares';
import { usd } from '../utils/formatos';

/**
 * Comercia › Logística › Origen y destino (maqueta de Melanie): de qué ciudad y
 * puerto sale, a qué ciudad llega y por qué puerto entra. «¿Para qué me sirve
 * esto? Para que luego te diga cuánto te podría costar la ruta.»
 *
 * Sólo ofrece lugares que tienen tarifa en el tarifario: un puerto sin tarifa
 * terminaría en una pantalla de Rutas vacía.
 */
export function OrigenDestinoPage() {
  return (
    <MarcoDeComercia>
      <Contenido />
    </MarcoDeComercia>
  );
}

interface Seleccion {
  origenId: string | null;
  destinoId: string | null;
  puntoLlegadaId: string | null;
  almacen: string;
}

function Contenido() {
  const { importacion, ultima, aplicar } = useImportacion();
  const { toast } = useToast();
  const [lugares, setLugares] = useState<LugaresDeComercia | null>(null);
  const [errorLugares, setErrorLugares] = useState<string | null>(null);
  const [vias, setVias] = useState<ViasDeComercia | null>(null);
  const [sel, setSel] = useState<Seleccion>(() => ({
    origenId: importacion?.origen?.id ?? null,
    destinoId: importacion?.destino?.id ?? null,
    puntoLlegadaId: importacion?.puntoLlegada?.id ?? null,
    almacen: importacion?.almacen ?? '',
  }));
  const [paisOrigen, setPaisOrigen] = useState<string | null>(importacion?.origen?.pais ?? null);
  const [ciudadOrigen, setCiudadOrigen] = useState<string | null>(
    importacion?.origen ? ciudadDe(importacion.origen) : null
  );
  const [paisDestino, setPaisDestino] = useState<string | null>(importacion?.destino?.pais ?? null);
  const [guardando, setGuardando] = useState(false);
  const primeraVez = useRef(true);
  /*
    El puerto de llegada se sugiere solo hasta que la persona elige uno. Si cambia
    el origen o el destino, la sugerencia vuelve a ser la del recorrido nuevo: antes
    quedaba la del destino anterior (Cochabamba → Santa Cruz seguía en Arica cuando
    lo más barato para Santa Cruz es Iquique).
  */
  const llegadaAMano = useRef(!!importacion?.puntoLlegada);
  const recorrido = useRef(`${sel.origenId}|${sel.destinoId}`);

  useEffect(() => {
    comerciaService
      .lugares()
      .then(setLugares)
      .catch((e) => setErrorLugares(mensajeDeError(e, 'No se pudo leer el tarifario.')));
  }, []);

  // Los puertos de llegada posibles, con el sugerido (el de la ruta más barata).
  useEffect(() => {
    if (!sel.origenId || !sel.destinoId) {
      setVias(null);
      return;
    }
    const clave = `${sel.origenId}|${sel.destinoId}`;
    if (clave !== recorrido.current) {
      recorrido.current = clave;
      llegadaAMano.current = false;
    }
    let vigente = true;
    comerciaService
      .vias(sel.origenId, sel.destinoId)
      .then((v) => {
        if (!vigente) return;
        setVias(v);
        // Mientras nadie eligió, se propone el puerto sugerido (como la maqueta).
        setSel((s) =>
          llegadaAMano.current &&
          (s.puntoLlegadaId === null || v.vias.some((x) => x.puerto.id === s.puntoLlegadaId))
            ? s
            : { ...s, puntoLlegadaId: v.sugeridoId }
        );
      })
      .catch(() => vigente && setVias(null));
    return () => {
      vigente = false;
    };
  }, [sel.origenId, sel.destinoId]);

  // Se guarda solo, en cuanto hay origen y destino: el botón de seguir no espera.
  useEffect(() => {
    if (primeraVez.current) {
      primeraVez.current = false;
      return;
    }
    if (!sel.origenId || !sel.destinoId) return;
    const t = setTimeout(async () => {
      setGuardando(true);
      try {
        aplicar(
          await comerciaService.guardarOrigenDestino({
            origenId: sel.origenId!,
            destinoId: sel.destinoId!,
            puntoLlegadaId: sel.puntoLlegadaId,
            almacen: sel.almacen.trim() || null,
          })
        );
      } catch (e) {
        toast({
          title: 'No se pudo guardar el origen y destino',
          description: mensajeDeError(e, 'Vuelve a intentar.'),
          variant: 'destructive',
        });
      } finally {
        setGuardando(false);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [sel, aplicar, toast]);

  const porId = useMemo(() => {
    const m = new Map<string, LugarDeComercia>();
    for (const l of [
      ...(lugares?.origenes ?? []),
      ...(lugares?.puertos ?? []),
      ...(lugares?.destinos ?? []),
    ])
      m.set(l.id, l);
    return m;
  }, [lugares]);

  const origen = sel.origenId ? (porId.get(sel.origenId) ?? null) : null;
  const destino = sel.destinoId ? (porId.get(sel.destinoId) ?? null) : null;
  const puerto = sel.puntoLlegadaId ? (porId.get(sel.puntoLlegadaId) ?? null) : null;
  const puertoDelMapa = puerto ?? vias?.vias[0]?.puerto ?? null;

  const origenes = lugares?.origenes ?? [];
  const paisesOrigen = unicos(origenes.map((o) => o.pais ?? ''));
  const ciudadesOrigen = unicos(
    origenes.filter((o) => (o.pais ?? '') === (paisOrigen ?? '')).map(ciudadDe)
  );
  const puntosOrigen = origenes.filter(
    (o) => (o.pais ?? '') === (paisOrigen ?? '') && ciudadDe(o) === ciudadOrigen
  );
  const destinos = lugares?.destinos ?? [];
  const paisesDestino = unicos(destinos.map((d) => d.pais ?? ''));
  const ciudadesDestino = destinos.filter((d) => (d.pais ?? '') === (paisDestino ?? ''));
  const modosDeSalida = new Set(
    origen ? (origenes.find((o) => o.id === origen.id)?.modos ?? []) : []
  );

  const elegirOrigen = (id: string | null) => setSel((s) => ({ ...s, origenId: id }));

  const usarUltima = () => {
    if (!ultima) return;
    setPaisOrigen(ultima.origen.pais);
    setCiudadOrigen(ciudadDe(ultima.origen));
    setPaisDestino(ultima.destino.pais);
    // El puerto de la última importación se respeta: no es una sugerencia.
    recorrido.current = `${ultima.origen.id}|${ultima.destino.id}`;
    llegadaAMano.current = true;
    setSel({
      origenId: ultima.origen.id,
      destinoId: ultima.destino.id,
      puntoLlegadaId: ultima.puntoLlegada?.id ?? null,
      almacen: ultima.almacen ?? '',
    });
  };

  const listo = !!importacion?.origen && !!importacion.destino && !guardando;
  const viasTexto = vias?.vias.map((v) => ciudadDe(v.puerto)) ?? [];

  return (
    <>
      <EncabezadoDeComercia
        etiqueta="Comercia · Logística"
        titulo="Origen y destino"
        acumulado={
          importacion
            ? {
                total: importacion.resumen.acumulado,
                fob: importacion.resumen.fob,
                moneda: importacion.moneda,
              }
            : { total: 0, fob: 0 }
        }
      />

      <PasosDeLogistica
        actual="origen-destino"
        importacion={importacion}
        siguiente={{
          texto: 'Continuar',
          href: '/comercia/logistica/carga',
          deshabilitado: !listo,
          aviso:
            !sel.origenId || !sel.destinoId
              ? 'Elige el origen y el destino'
              : guardando
                ? 'Guardando…'
                : undefined,
        }}
      />

      {errorLugares && (
        <p role="alert" className="rounded-xl bg-bloqueo-suave px-4 py-3 text-sm text-bloqueo">
          {errorLugares}
        </p>
      )}
      {lugares && lugares.origenes.length === 0 && (
        <p className="rounded-xl bg-alerta-suave px-4 py-3 text-sm text-alerta">
          Tu tarifario todavía no tiene rutas.{' '}
          <Link to="/trade/tarifario" className="font-semibold underline underline-offset-2">
            Cárgalo en Datos Maestros › Tarifario
          </Link>{' '}
          y vuelve acá.
        </p>
      )}

      {/*
        45 / 55, como la maqueta: el mapa es la columna ancha. Con la izquierda
        fija en 640 px (el ancho de la captura, que está al 135 %) el recorrido
        de arriba del mapa no entraba y se cortaba en «Sha…» y «Ari…».
      */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,9fr)_minmax(0,11fr)]">
        <div className="space-y-5">
          {ultima && (
            <button
              type="button"
              onClick={usarUltima}
              className="inline-flex max-w-full items-center gap-2 rounded-full border border-dashed bg-card px-5 py-2.5 text-[15px] shadow-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <RotateCcw className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="font-semibold">Usar el de la última importación</span>
              <span className="hidden truncate text-muted-foreground sm:inline">
                · {ciudadDe(ultima.origen)} → {ciudadDe(ultima.destino)}
              </span>
            </button>
          )}

          <Tarjeta titulo="Origen" tono="naranja">
            <div className="grid gap-4 sm:grid-cols-2">
              <Selector
                etiqueta="País"
                valor={paisOrigen ?? ''}
                onCambio={(v) => {
                  setPaisOrigen(v || null);
                  setCiudadOrigen(null);
                  elegirOrigen(null);
                }}
                opciones={paisesOrigen.map((p) => ({
                  valor: p,
                  texto: nombreDePais(p),
                  prefijo: p,
                }))}
                vacio="Elige el país"
              />
              <Selector
                etiqueta="Ciudad"
                valor={ciudadOrigen ?? ''}
                onCambio={(v) => {
                  setCiudadOrigen(v || null);
                  const puntos = origenes.filter(
                    (o) => (o.pais ?? '') === (paisOrigen ?? '') && ciudadDe(o) === v
                  );
                  elegirOrigen(puntos.length === 1 ? puntos[0].id : null);
                }}
                opciones={ciudadesOrigen.map((c) => ({ valor: c, texto: c }))}
                vacio="Elige la ciudad"
                deshabilitado={!paisOrigen}
              />
            </div>
            <Selector
              etiqueta="Punto de salida"
              extra={
                <Modos activos={origen ? [modoDelPunto(origen)] : []} disponibles={modosDeSalida} />
              }
              valor={sel.origenId ?? ''}
              onCambio={(v) => elegirOrigen(v || null)}
              opciones={puntosOrigen.map((o) => ({ valor: o.id, texto: rotuloDelPunto(o) }))}
              vacio="Elige el puerto de salida"
              deshabilitado={!ciudadOrigen}
            />
          </Tarjeta>

          <Tarjeta titulo="Destino" tono="violeta">
            <div className="grid gap-4 sm:grid-cols-2">
              <Selector
                etiqueta="País"
                valor={paisDestino ?? ''}
                onCambio={(v) => {
                  setPaisDestino(v || null);
                  setSel((s) => ({ ...s, destinoId: null }));
                }}
                opciones={paisesDestino.map((p) => ({
                  valor: p,
                  texto: nombreDePais(p),
                  prefijo: p,
                }))}
                vacio="Elige el país"
              />
              <Selector
                etiqueta="Ciudad"
                valor={sel.destinoId ?? ''}
                onCambio={(v) => setSel((s) => ({ ...s, destinoId: v || null }))}
                opciones={ciudadesDestino.map((d) => ({ valor: d.id, texto: ciudadDe(d) }))}
                vacio="Elige la ciudad"
                deshabilitado={!paisDestino}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Selector
                etiqueta="Punto de llegada"
                extra={
                  <span className="flex items-center gap-2">
                    {vias?.sugeridoId && sel.puntoLlegadaId === vias.sugeridoId && (
                      <span className="text-[11px] font-bold tracking-[0.08em] text-muted-foreground">
                        SUGERIDO
                      </span>
                    )}
                    <Modos
                      activos={puerto ? [modoDelPunto(puerto)] : []}
                      disponibles={new Set(['SEA', 'AIR', 'LAND'])}
                    />
                  </span>
                }
                valor={sel.puntoLlegadaId ?? ''}
                onCambio={(v) => {
                  llegadaAMano.current = true;
                  setSel((s) => ({ ...s, puntoLlegadaId: v || null }));
                }}
                opciones={(vias?.vias ?? []).map((v) => ({
                  valor: v.puerto.id,
                  texto: `${rotuloDelPunto(v.puerto)} · ${nombreDePais(v.puerto.pais)}`,
                }))}
                vacio="Cualquiera: te mostramos todas"
                vacioElegible
                deshabilitado={!vias || vias.vias.length === 0}
                ayuda={
                  puerto && vias
                    ? (() => {
                        const v = vias.vias.find((x) => x.puerto.id === puerto.id);
                        return v?.desde != null
                          ? `Desde ${usd(v.desde)} por contenedor${vias.contenedor ? ` de ${vias.contenedor}` : ''}, según tu tarifario.`
                          : undefined;
                      })()
                    : 'Te mostramos las rutas por todos los puertos.'
                }
              />
              <label className="block">
                <span className="mb-2 block text-xs font-semibold tracking-[0.08em] text-muted-foreground">
                  ALMACÉN / DESTINO FINAL
                </span>
                <input
                  value={sel.almacen}
                  onChange={(e) => setSel((s) => ({ ...s, almacen: e.target.value }))}
                  list="comercia-almacenes"
                  placeholder="Tu almacén (opcional)"
                  maxLength={160}
                  // Un nombre largo se corta con «…» y se lee entero al pasar el mouse.
                  title={sel.almacen || undefined}
                  className="h-12 w-full text-ellipsis rounded-xl border border-input bg-campo px-4 text-[15px] font-semibold placeholder:font-normal placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                <datalist id="comercia-almacenes">
                  {(lugares?.almacenes ?? []).map((a) => (
                    <option key={a} value={a} />
                  ))}
                </datalist>
              </label>
            </div>
          </Tarjeta>
        </div>

        <section
          aria-label="Recorrido"
          className="space-y-4 self-start rounded-tarjeta border bg-card p-5 shadow-sm"
        >
          <Recorrido
            origen={origen}
            puerto={puertoDelMapa}
            destino={destino}
            almacen={sel.almacen}
          />
          <MapaDeRuta origen={origen} puerto={puertoDelMapa} destino={destino} />
          {origen && destino && vias && (
            <p className="flex items-start gap-2 text-sm text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span>
                {vias.rutas === 0 ? (
                  <>
                    No hay tarifas para ir de {ciudadDe(origen)} a {ciudadDe(destino)}. Cárgalas en
                    el tarifario.
                  </>
                ) : (
                  <>
                    Con este origen y destino hay{' '}
                    <strong className="text-foreground">
                      {vias.rutas} {vias.rutas === 1 ? 'ruta posible' : 'rutas posibles'}
                    </strong>
                    {viasTexto.length > 0 && <>: por {unirConO(viasTexto)}</>}.
                    {destino.pais === 'BO' && ' Bolivia no tiene mar: la carga entra por Chile.'}
                  </>
                )}
              </span>
            </p>
          )}
        </section>
      </div>
    </>
  );
}

function unicos(xs: string[]) {
  return [...new Set(xs.filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es'));
}

function unirConO(xs: string[]) {
  if (xs.length <= 1) return xs.join('');
  return `${xs.slice(0, -1).join(', ')} o por ${xs[xs.length - 1]}`;
}

function Tarjeta({
  titulo,
  tono,
  children,
}: {
  titulo: string;
  tono: 'naranja' | 'violeta';
  children: ReactNode;
}) {
  return (
    <section aria-label={titulo} className="space-y-4 rounded-tarjeta border bg-card p-5 shadow-sm">
      <h2 className="flex items-center gap-3 font-titulo text-lg font-semibold">
        <span
          className={cn(
            'grid h-9 w-9 place-items-center rounded-xl text-white',
            tono === 'naranja' ? 'bg-orange-700' : 'bg-primary'
          )}
        >
          <MapPin className="h-4 w-4" aria-hidden="true" />
        </span>
        {titulo}
      </h2>
      {children}
    </section>
  );
}

function Selector({
  etiqueta,
  extra,
  valor,
  onCambio,
  opciones,
  vacio,
  vacioElegible,
  deshabilitado,
  ayuda,
}: {
  etiqueta: string;
  extra?: ReactNode;
  valor: string;
  onCambio: (v: string) => void;
  opciones: Array<{ valor: string; texto: string; prefijo?: string }>;
  vacio: string;
  /** El vacío es una opción válida («cualquiera»), no un «elige algo». */
  vacioElegible?: boolean;
  deshabilitado?: boolean;
  /** Una línea debajo, con lo que conviene saber de lo elegido. */
  ayuda?: string;
}) {
  return (
    <label className="block">
      <span className="mb-2 flex min-h-6 items-center justify-between gap-2">
        <span className="text-xs font-semibold tracking-[0.08em] text-muted-foreground">
          {etiqueta.toLocaleUpperCase('es')}
        </span>
        {extra}
      </span>
      <select
        value={valor}
        onChange={(e) => onCambio(e.target.value)}
        disabled={deshabilitado}
        className="h-12 w-full rounded-xl border border-input bg-campo px-4 text-[15px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
      >
        <option value="" disabled={!vacioElegible}>
          {vacio}
        </option>
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.prefijo ? `${o.prefijo} ${o.texto}` : o.texto}
          </option>
        ))}
      </select>
      {ayuda && <span className="mt-1.5 block text-xs text-muted-foreground">{ayuda}</span>}
    </label>
  );
}

/** Los tres íconos de la maqueta: barco, avión, camión. Resalta por dónde se sale o se llega. */
function Modos({ activos, disponibles }: { activos: string[]; disponibles: Set<string> }) {
  const items = [
    { modo: 'SEA', Icono: Ship, nombre: 'Marítimo' },
    { modo: 'AIR', Icono: Plane, nombre: 'Aéreo' },
    { modo: 'LAND', Icono: Truck, nombre: 'Terrestre' },
  ];
  return (
    <span className="flex items-center gap-1">
      {items.map(({ modo, Icono, nombre }) => {
        const activo = activos.includes(modo);
        const hay = disponibles.has(modo);
        return (
          <span
            key={modo}
            title={hay ? nombre : `${nombre}: sin tarifas en tu tarifario`}
            className={cn(
              'grid h-6 w-6 place-items-center rounded-md',
              activo
                ? 'bg-primary/10 text-primary'
                : hay
                  ? 'text-muted-foreground'
                  : 'text-muted-foreground/40'
            )}
          >
            <Icono className="h-3.5 w-3.5" aria-label={nombre} />
          </span>
        );
      })}
    </span>
  );
}

/** «CN Shenzhen —mar→ CL Arica —camión→ BO Santa Cruz». */
function Recorrido({
  origen,
  puerto,
  destino,
  almacen,
}: {
  origen: LugarDeComercia | null;
  puerto: LugarDeComercia | null;
  destino: LugarDeComercia | null;
  almacen: string;
}) {
  const parada = (l: LugarDeComercia | null, detalle: string) => (
    <div className="min-w-0 text-center">
      <p className="text-xl font-semibold leading-none">{l?.pais ?? '··'}</p>
      <p className="mt-1 truncate font-titulo text-xl font-semibold">{l ? ciudadDe(l) : '—'}</p>
      <p className="truncate text-sm text-muted-foreground" title={l ? detalle : undefined}>
        {l ? detalle : 'pendiente'}
      </p>
    </div>
  );
  const tramo = (Icono: typeof Ship, texto: string) => (
    <div className="flex w-[72px] flex-col items-center gap-1 text-sm text-muted-foreground sm:w-[88px]">
      <Icono className="h-4 w-4" aria-hidden="true" />
      <span className="flex w-full items-center">
        <span className="h-px flex-1 bg-border" />
        <ChevronRight className="-ml-1 h-3.5 w-3.5" aria-hidden="true" />
      </span>
      <span>{texto}</span>
    </div>
  );
  /*
    Tres paradas del mismo ancho y los tramos fijos: con flex, el detalle largo
    del destino («Bolivia · Almacén central · Parque…») se comía el ancho y el
    origen quedaba en «Shang…».
  */
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1 px-2">
      {parada(origen, origen ? `${nombreDePais(origen.pais)} · ${nombreDelPunto(origen)}` : '')}
      {tramo(Ship, 'mar')}
      {parada(puerto, puerto ? `${nombreDePais(puerto.pais)} · puerto` : '')}
      {tramo(Truck, 'camión')}
      {parada(
        destino,
        destino ? `${nombreDePais(destino.pais)} · ${almacen.trim() || 'destino'}` : ''
      )}
    </div>
  );
}
